import { Licenses, BillingCycle, AddOns } from "@nova/constants"
import { selectAllAddOns, selectAllLicenses } from "features/entity-caching/licenses/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"

export type TableBillingCycle = `${BillingCycle}` | "both"
export type TableData = {
    assigneeId?: string
    assigneeAttributes?: { fullName: string; email: string }
    license?: { licenseId: string; subscriptionId: string; licenseType: Licenses }
    addOns: { licenseId: string; subscriptionId: string; addOnType: AddOns }[]
    usage?: number
    billingCycle: TableBillingCycle
}

export const useLicensesAndAddonsTableData = () => {
    const licenses = useAppSelector(selectAllLicenses)
    const addOns = useAppSelector(selectAllAddOns)
    const nonLiteLicenses = useMemo(
        () =>
            Object.fromEntries(
                Object.entries(licenses).filter(([_, l]) => l.subscriptionType !== Licenses.Lite)
            ),
        [licenses]
    )
    const assignedTableData = useMemo(() => {
        return [...Object.values(nonLiteLicenses), ...Object.values(addOns)].reduce(
            (acc, cur) => {
                if (!cur.assigneeId) return acc
                const existingEntryForUser = acc[cur.assigneeId]
                if (!existingEntryForUser) {
                    acc[cur.assigneeId] = {
                        assigneeId: cur.assigneeId,
                        assigneeAttributes: cur.assigneeAttributes,
                        license: cur.isAddOn
                            ? undefined
                            : {
                                  licenseId: cur.licenseId,
                                  subscriptionId: cur.subscriptionId,
                                  licenseType: cur.subscriptionType as Licenses,
                              },
                        addOns: cur.isAddOn
                            ? [
                                  {
                                      licenseId: cur.licenseId,
                                      subscriptionId: cur.subscriptionId,
                                      addOnType: cur.subscriptionType as AddOns,
                                  },
                              ]
                            : [],
                        usage: cur.isAddOn ? undefined : cur.usageAmount,
                        billingCycle: `${cur.billingCycle}` as TableBillingCycle,
                    }
                } else {
                    acc[cur.assigneeId] = {
                        ...existingEntryForUser,
                        license: cur.isAddOn
                            ? existingEntryForUser.license
                            : {
                                  licenseId: cur.licenseId,
                                  subscriptionId: cur.subscriptionId,
                                  licenseType: cur.subscriptionType as Licenses,
                              },
                        addOns: cur.isAddOn
                            ? [
                                  ...existingEntryForUser.addOns,
                                  {
                                      licenseId: cur.licenseId,
                                      subscriptionId: cur.subscriptionId,
                                      addOnType: cur.subscriptionType as AddOns,
                                  },
                              ]
                            : existingEntryForUser.addOns,
                        usage: cur.isAddOn ? existingEntryForUser.usage : cur.usageAmount,
                        billingCycle:
                            cur.billingCycle === existingEntryForUser.billingCycle
                                ? existingEntryForUser.billingCycle
                                : "both",
                    }
                }
                return acc
            },
            {} as Record<string, TableData>
        )
    }, [addOns, nonLiteLicenses])
    const unassignedTableData = useMemo(() => {
        return [...Object.values(nonLiteLicenses), ...Object.values(addOns)]
            .filter((l) => !l.assigneeId)
            .map<TableData>((l) => ({
                assigneeId: undefined,
                license: l.isAddOn
                    ? undefined
                    : {
                          licenseId: l.licenseId,
                          subscriptionId: l.subscriptionId,
                          licenseType: l.subscriptionType as Licenses,
                      },
                addOns: l.isAddOn
                    ? [
                          {
                              licenseId: l.licenseId,
                              subscriptionId: l.subscriptionId,
                              addOnType: l.subscriptionType as AddOns,
                          },
                      ]
                    : [],
                usage: l.isAddOn ? undefined : l.usageAmount,
                billingCycle: l.billingCycle,
            }))
    }, [addOns, nonLiteLicenses])
    const tableData = useMemo(
        () =>
            [...Object.values(assignedTableData), ...unassignedTableData].filter(
                (entry) => !!entry
            ),
        [assignedTableData, unassignedTableData]
    )
    return tableData
}
